/**
 * Emoji utilities for consistent rendering across the application
 */

/**
 * Normalizes text to ensure consistent emoji representation
 * Uses NFC normalization for Unicode consistency
 */
export const normalizeEmoji = (text: string): string => {
  if (!text) return text;

  try {
    // Normalize to NFC form for consistent emoji representation
    return text.normalize('NFC');
  } catch (error) {
    // Fallback to original text if normalization fails
    return text;
  }
};

/**
 * Comprehensive emoji detection and processing for all emoji types
 * Including: basic emojis, skin tones, ZWJ sequences, flags, symbols, and recent emojis
 */
export const isValidEmoji = (char: string): boolean => {
  if (!char) return false;

  // Comprehensive emoji regex covering all Unicode emoji ranges
  const emojiRegex = /(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|\ud83c[\ude32-\ude3a]|\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff]|[\u2300-\u23ff]|[\u2460-\u24ff]|\u25a0|\u25a1|[\u25b2-\u25ff]|[\u2600-\u26ff]|\u2700|[\u2701-\u27ff]|[\u2900-\u297f]|[\u2b00-\u2bff]|\u3030|\u303d|\u3297|\u3299|[\u00a0-\u00ff]|[\u2000-\u206f]|[\u2100-\u214f]|[\u2190-\u21ff]|[\u2200-\u22ff]|[\u2300-\u23ff]|[\u2400-\u243f]|[\u2440-\u245f]|[\u2460-\u24ff]|[\u2500-\u257f]|[\u2580-\u259f]|[\u25a0-\u25ff]|[\u2600-\u26ff]|[\u2700-\u27bf]|[\u2800-\u28ff]|[\u2900-\u297f]|[\u2b00-\u2bff]|[\u2e00-\u2e7f]|[\u3000-\u303f]|[\u3040-\u309f]|[\u30a0-\u30ff]|[\u3100-\u312f]|[\u3130-\u318f]|[\u3190-\u319f]|[\u31a0-\u31bf]|[\u31c0-\u31ef]|[\u31f0-\u31ff]|[\u3200-\u32ff]|[\u3300-\u33ff]|[\u3400-\u4dbf]|[\u4e00-\u9fff]|[\ua000-\ua48f]|[\ua490-\ua4cf]|[\ua4d0-\ua4ff]|[\ua500-\ua63f]|[\ua640-\ua69f]|[\ua6a0-\ua6ff]|[\ua700-\ua71f]|[\ua720-\ua7ff]|[\ua800-\ua82f]|[\ua830-\ua83f]|[\ua840-\ua87f]|[\ua880-\ua8df]|[\ua8e0-\ua8ff]|[\ua900-\ua92f]|[\ua930-\ua95f]|[\ua960-\ua97f]|[\ua980-\ua9df]|[\ua9e0-\ua9ff]|[\uaa00-\uaa5f]|[\uaa60-\uaa7f]|[\uaa80-\uaadf]|[\uaae0-\uaaff]|[\uab00-\uab2f]|[\uab30-\uab6f]|[\uab70-\uabbf]|[\uabc0-\uabff]|[\uac00-\ud7af]|[\ud7b0-\ud7ff]|[\uf900-\ufaff]|[\ufb00-\ufb4f]|[\ufb50-\ufdff]|[\ufe70-\ufeff]|[\uff00-\uffef])/g;

  return emojiRegex.test(char);
};

/**
 * Comprehensive text sanitization for all emoji-related issues
 * Handles: replacement characters, null bytes, skin tones, ZWJ sequences, encoding issues
 */
export const sanitizeEmoji = (text: string): string => {
  if (!text) return text;

  let sanitized = text;

  // Remove null bytes that can corrupt emoji rendering
  sanitized = sanitized.replace(/\u0000/g, '');

  // Replace Unicode replacement characters with question mark emoji
  sanitized = sanitized.replace(/�/g, '❓');

  // Handle problematic skin tone modifiers that might cause rendering issues
  // Keep skin tones but ensure they're properly formatted
  sanitized = sanitized.replace(/[\ud83c][\udffb-\udfff]/g, (match) => {
    try {
      return match.normalize('NFC');
    } catch {
      return ''; // Remove problematic skin tone if normalization fails
    }
  });

  // Handle ZWJ (Zero Width Joiner) sequences that might be corrupted
  // Fix common ZWJ issues while preserving valid sequences
  sanitized = sanitized.replace(/\u200D+/g, '\u200D'); // Normalize multiple ZWJs to single

  // Remove text-presentation variation selector (FE0E) to force color emoji rendering.
  // Preserve FE0F (color-presentation selector) as it's needed for colored emojis.
  sanitized = sanitized.replace(/\uFE0E/g, '');

  return sanitized;
};

/**
 * Complete emoji processing pipeline
 */
export const processEmojisInText = (text: string): string => {
  if (!text) return text;

  // Step 1: Normalize Unicode
  let processed = normalizeEmoji(text);

  // Step 2: Sanitize problematic characters
  processed = sanitizeEmoji(processed);

  return processed;
};

/**
 * Safe emoji rendering for React components
 * Returns the processed text for consistent display
 */
export const renderEmojiWithFallback = (text: string): string => {
  return processEmojisInText(text);
};
